import * as SheetPrimitive from "@radix-ui/react-dialog";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronUp, Heart, Link2, MapPin, Trash2, X } from "lucide-react";
import { fireEvent } from "@/lib/events";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { fetchMemoryDetail, fetchCardContext } from "@/hooks/queries/useMemoryDetailData";
import { fetchActiveShare, fetchShareDetails } from "@/hooks/queries/useSharedMemories";
import { useMemoryArtefacts, type MemoryArtefact } from "@/hooks/queries/useMemoryArtefacts";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import { DataView } from "@/components/DataView";
import { retryAiProcessing } from "@/lib/captureProcessor";
import { useAuth } from "@/hooks/useAuth";
import { useActiveSpace } from "@/hooks/useActiveSpace";
import { useCategories } from "@/hooks/useCategories";
import { useChildren } from "@/hooks/useChildren";
import { useEntitlements, invalidateEntitlements } from "@/hooks/useEntitlements";
import { invalidateMemories } from "@/hooks/useMemories";
import ChildAvatar from "@/components/ChildAvatar";
import ShortiePlayer from "@/components/ShortiePlayer";
import FullScreenImage from "@/components/FullScreenImage";
import ParticipantPicker from "@/components/ParticipantPicker";
import CollectionPicker from "@/components/CollectionPicker";
import VisibilityReadout from "@/components/capture/VisibilityReadout";
import ShareWithTaggedToggle from "@/components/capture/ShareWithTaggedToggle";
import { SheetOverlay, SheetPortal } from "@/components/ui/sheet";
import { useAiProcessingGate } from "@/hooks/useAiProcessingGate";
import { AiOffBottomSheet } from "@/components/AiOffBottomSheet";
import { useCollections } from "@/hooks/useCollections";
import { useToggleFavorite } from "@/hooks/queries/useToggleFavorite";
import { useTribeMembers } from "@/hooks/useTribeMembers";
import { useCurrentRole } from "@/hooks/useCurrentRole";
import type { ParticipantRef } from "@/types/participant";
import { getMemoryAutoTitle } from "@/lib/memoryTitle";

// ── Types ─────────────────────────────────────────────────────────────────────

interface DetailMemory {
  id: string;
  user_id: string;
  title: string | null;
  ai_chapter: string | null;
  mori_comment: string | null;
  raw_transcript: string | null;
  categories: string[];
  audio_url: string | null;
  context_audio_url: string | null;
  image_url: string | null;
  memory_type: string;
  capsule_state: string;
  created_at: string;
  memory_date: string | null;
  contributor_name: string | null;
  location: string | null;
  video_url: string | null;
  thumbnail_url: string | null;
  video_duration_seconds: number | null;
  content_library_id: string | null;
  ai_status: string | null;
  ai_retry_count: number | null;
  preserve_audio: boolean;
  audience: "members" | "space" | null;
  memory_participants: Array<{
    id: string;
    child_id: string | null;
    space_member_id: string | null;
    display_name: string | null;
    children: { name: string; avatar_url: string | null; avatar_color: string | null } | null;
    space_members: { id: string; display_name: string | null; user_id: string | null } | null;
  }>;
  collection_memories: Array<{
    collection_id: string;
    collection: { id: string; name: string; icon: string; color: string } | null;
  }>;
  is_favorited: boolean;
}

type SaveStatus = "idle" | "saving" | "saved" | "error";

// ── Constants ─────────────────────────────────────────────────────────────────

const DETAIL_SELECT =
  "*, memory_participants(id,child_id,space_member_id,display_name,children:children(id,name,avatar_url,avatar_color),space_members:space_members(id,display_name,user_id)), collection_memories(collection_id, collection:collections(id, name, icon, color))";

const categoryColorClass: Record<string, string> = {
  Milestone:    "bg-category-milestone text-category-milestone-text border-transparent",
  Funny:        "bg-category-funny text-category-funny-text border-transparent",
  Drawing:      "bg-category-drawing text-category-drawing-text border-transparent",
  Conversation: "bg-category-conversation text-category-conversation-text border-transparent",
  First:        "bg-category-first text-category-first-text border-transparent",
  Thankful:     "bg-category-thankful text-category-thankful-text border-transparent",
  Travel:       "bg-category-travel text-category-travel-text border-transparent",
  Achievement:  "bg-category-achievement text-category-achievement-text border-transparent",
  Holiday:      "bg-category-milestone text-category-milestone-text border-transparent",
};

const fallbackCategoryStyle = "bg-muted text-muted-foreground border-transparent";

function formatFull(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

// Day-only format used for the "Captured X" subtitle — no weekday/time,
// since the exact moment of save is rarely what the reader cares about.
function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

// True when memoryDate and createdAt fall on different calendar days —
// same-day captures are the norm and showing "Captured same day" is noise.
function isDifferentCaptureDay(memoryDate: string, createdAt: string): boolean {
  const a = new Date(memoryDate);
  const b = new Date(createdAt);
  return (
    a.getFullYear() !== b.getFullYear() ||
    a.getMonth() !== b.getMonth() ||
    a.getDate() !== b.getDate()
  );
}

/**
 * Translate non-child `memory_participants` rows into picker-ready ParticipantRefs.
 * Rows with child_id set are children (handled by the child picker, not here).
 * Rows with space_member_id are matched against tribe members for chip rendering.
 * Rows with display_name only are free-text refs.
 * Owner is implicit (memories.user_id) — no explicit row is expected.
 */
function rowsToRefs(
  rows: Array<{
    id: string;
    child_id: string | null;
    space_member_id: string | null;
    display_name: string | null;
  }>,
  coparents: Array<{ connectionId: string; profileId: string | null; role: "member" | "contributor"; name: string }>,
  contributors: Array<{ connectionId: string; profileId: string | null; role: "member" | "contributor"; name: string }>,
): ParticipantRef[] {
  const byMemberId = new Map<string, { connectionId: string; profileId: string | null; role: "member" | "contributor"; name: string }>();
  for (const m of [...coparents, ...contributors]) {
    byMemberId.set(m.connectionId, m);
  }
  const refs: ParticipantRef[] = [];
  for (const row of rows) {
    // Skip child rows — they are managed by the child picker.
    if (row.child_id !== null) continue;
    if (row.space_member_id) {
      const m = byMemberId.get(row.space_member_id);
      if (m) {
        refs.push({
          kind: "connection",
          connectionId: m.connectionId,
          profileId: m.profileId,
          role: m.role,
          name: m.name,
        });
        continue;
      }
      // Space member row exists but is no longer in the tribe (revoked/removed).
      // Preserve as a name ref so the edit view renders something.
      refs.push({ kind: "name", name: "Former tribe member" });
      continue;
    }
    if (row.display_name) {
      refs.push({ kind: "name", name: row.display_name });
    }
  }
  return refs;
}

// ── Artefact carousel sub-components ─────────────────────────────────────────

function ArtefactSlide({
  storagePath,
  onTap,
}: {
  storagePath: string;
  onTap: (signedUrl: string) => void;
}) {
  const { t } = useTranslation();
  const [signedUrl, setSignedUrl] = useState<string | null>(null);

  useEffect(() => {
    supabase.storage
      .from("memory-images")
      .createSignedUrl(storagePath, 3600)
      .then(({ data }) => { if (data?.signedUrl) setSignedUrl(data.signedUrl); });
  }, [storagePath]);

  return (
    <button
      type="button"
      onClick={() => { if (signedUrl) onTap(signedUrl); }}
      className="w-full rounded-xl overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      aria-label={t("aria.viewFullScreen")}
      disabled={!signedUrl}
    >
      {signedUrl ? (
        <img
          src={signedUrl}
          alt={t("alt.memoryPhoto")}
          className="w-full object-cover rounded-xl"
          style={{ maxHeight: 240 }}
        />
      ) : (
        <div className="w-full rounded-xl bg-muted/30 animate-pulse" style={{ minHeight: 160 }} />
      )}
    </button>
  );
}

function ArtefactCarousel({
  artefacts,
  onTap,
}: {
  artefacts: MemoryArtefact[];
  onTap: (signedUrl: string) => void;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [api, setApi] = useState<CarouselApi | undefined>();
  const isMulti = artefacts.length > 1;

  useEffect(() => {
    if (!api) return;
    const onSelect = () => setCurrentIndex(api.selectedScrollSnap());
    api.on("select", onSelect);
    setCurrentIndex(api.selectedScrollSnap());
    return () => { api.off("select", onSelect); };
  }, [api]);

  return (
    <div className="space-y-2">
      <Carousel setApi={setApi} opts={{ loop: false }}>
        <CarouselContent>
          {artefacts.map((artefact) => (
            <CarouselItem key={artefact.id}>
              <ArtefactSlide storagePath={artefact.storage_path} onTap={onTap} />
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
      {isMulti && (
        <div className="flex justify-center gap-1.5">
          {artefacts.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`h-1.5 rounded-full transition-all ${i === currentIndex ? "w-4 bg-primary" : "w-1.5 bg-muted-foreground/30"}`}
              onClick={() => api?.scrollTo(i)}
              aria-label={`Go to photo ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface MemoryDetailSheetProps {
  memoryId: string;
  isOpen: boolean;
  onClose: () => void;
  /** Called after successful delete so the parent can do optimistic removal. */
  onDelete?: () => void;
  /** Open directly in edit mode (e.g. when user taps the Edit button on the card). */
  defaultEditMode?: boolean;
  /** When true, render as a full page instead of a Sheet overlay. */
  fullPage?: boolean;
}

// ── Component ─────────────────────────────────────────────────────────────────

const MemoryDetailSheet = ({
  memoryId,
  isOpen,
  onClose,
  onDelete,
  defaultEditMode = false,
  fullPage = false,
}: MemoryDetailSheetProps) => {
  const { t } = useTranslation();
  const { user, activeSpaceRole } = useAuth();
  const { space: activeSpace } = useActiveSpace();
  const { ensureAiOrPrompt, sheetOpen: aiSheetOpen, onEnableAi, onCancelSheet: onCancelAiSheet, enabling: aiEnabling, isOwner: aiIsOwner } = useAiProcessingGate(activeSpace?.id ?? null);
  const { activeCategories, getCategoryStyle } = useCategories();
  // `allChildren` here is the union of taggable subjects (children + pets +
  // later toys). Variable name kept for diff readability — the underlying
  // memory_participants.child_id column accepts any subject id.
  const { subjects: allChildren } = useChildren();
  const { spaceMemberId } = useCurrentRole();
  const taggableSubjects = useMemo(
    () =>
      spaceMemberId
        ? allChildren.filter((s) => s.promoted_to_space_member_id !== spaceMemberId)
        : allChildren,
    [allChildren, spaceMemberId],
  );
  const { owner, coparents, contributors } = useTribeMembers();
  const { canAI } = useEntitlements();
  const { collections, refresh: refreshCollections } = useCollections();
  const toggleFavorite = useToggleFavorite();
  const { artefacts, isLoading: artefactsLoading, isError: artefactsIsError, error: artefactsError, refetch: refetchArtefacts } = useMemoryArtefacts(memoryId);

  // ── Data state ──────────────────────────────────────────────────────────────
  const [memory, setMemory] = useState<DetailMemory | null>(null);
  const [loading, setLoading] = useState(false);
  // RLS-deny and genuinely-missing rows are indistinguishable from the client;
  // both surface here as a null row. We render the same "Memory not found"
  // copy for either (no information leak by design).
  const [notFound, setNotFound] = useState(false);

  // ── Edit state (mirrors DB fields) ──────────────────────────────────────────
  const [editMode, setEditMode] = useState(false);
  const [title, setTitle] = useState("");
  const [aiChapter, setAiChapter] = useState("");
  const [categories, setCategories] = useState<string[]>([]);
  const [taggedChildIds, setTaggedChildIds] = useState<string[]>([]);
  const [participants, setParticipants] = useState<ParticipantRef[]>([]);
  // Local yyyy-mm-dd string bound to <input type="date">. Empty = not loaded yet.
  const [memoryDateIso, setMemoryDateIso] = useState("");
  // ── Card context state ───────────────────────────────────────────────────────
  const [cardTitle, setCardTitle] = useState<string | null>(null);
  const [otherTimes, setOtherTimes] = useState<Array<{ id: string; title: string | null; created_at: string }>>([]);

  // ── UI state ─────────────────────────────────────────────────────────────────
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [voiceNoteOpen, setVoiceNoteOpen] = useState(false);
  const [contextAudioOpen, setContextAudioOpen] = useState(false);
  const [signedAudioUrl, setSignedAudioUrl] = useState<string | null>(null);
  const [signedContextAudioUrl, setSignedContextAudioUrl] = useState<string | null>(null);
  const [signedImageUrl, setSignedImageUrl] = useState<string | null>(null);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [deleteAudioOpen, setDeleteAudioOpen] = useState(false);
  const [deleteMemoryOpen, setDeleteMemoryOpen] = useState(false);
  // ── Visibility state ─────────────────────────────────────────────────────
  const [isSharedWithTagged, setIsSharedWithTagged] = useState(true);
  const [removeAccessDialogOpen, setRemoveAccessDialogOpen] = useState(false);

  // ── Share state ───────────────────────────────────────────────────────────
  interface ShareInfo {
    token: string;
    viewCount: number;
    expiresAt: string;
  }
  const [shareInfo, setShareInfo] = useState<ShareInfo | null | undefined>(undefined); // undefined = not loaded yet
  const [revokeLoading, setRevokeLoading] = useState(false);
  const [retryDismissed, setRetryDismissed] = useState(false);
  const [retryAiLoading, setRetryAiLoading] = useState(false);
  const [retryExhausted, setRetryExhausted] = useState(false);

  // ── Debounce refs ────────────────────────────────────────────────────────────
  const titleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const chapterTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clean up timers on unmount
  useEffect(() => () => {
    if (titleTimer.current) clearTimeout(titleTimer.current);
    if (chapterTimer.current) clearTimeout(chapterTimer.current);
    if (savedTimer.current) clearTimeout(savedTimer.current);
  }, []);

  // ── Fetch ────────────────────────────────────────────────────────────────────
  const fetchMemory = useCallback(async () => {
    if (!memoryId) return;
    const data = await fetchMemoryDetail(memoryId);
    if (!data) {
      setNotFound(true);
      return;
    }
    setNotFound(false);
    const m = data as DetailMemory;
    setMemory(m);
    setTitle(m.title ?? "");
    setAiChapter(m.ai_chapter ?? "");
    setCategories(m.categories ?? []);
    setTaggedChildIds(
      m.memory_participants.filter((mp) => mp.child_id !== null).map((mp) => mp.child_id!),
    );
    setParticipants(
      rowsToRefs(m.memory_participants.filter((mp) => mp.child_id === null), coparents, contributors),
    );
    // Prefer memory_date (user-editable); fall back to created_at for rows
    // captured before the column existed so the input always has a value.
    const sourceIso = m.memory_date ?? m.created_at;
    setMemoryDateIso(new Date(sourceIso).toISOString().slice(0, 10));
  }, [memoryId, user?.id, coparents, contributors]);

  // Fetch active share info so the bottom bar can show status + revoke option.
  const fetchShareInfo = useCallback(async () => {
    if (!user) return;
    const info = await fetchShareDetails(memoryId, user.id);
    setShareInfo(info);
  }, [memoryId, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset and fetch on open
  useEffect(() => {
    if (!isOpen || !memoryId) return;
    setEditMode(defaultEditMode);
    setTranscriptOpen(false);
    setVoiceNoteOpen(false);
    setContextAudioOpen(false);
    setSignedAudioUrl(null);
    setSignedContextAudioUrl(null);
    setSignedImageUrl(null);
    setLightboxSrc(null);
    setSaveStatus("idle");
    setRetryDismissed(false);
    setMemory(null);
    setNotFound(false);
    setShareInfo(undefined);
    setLoading(true);
    void refetchArtefacts();
    // Fetch memory and share info in parallel
    void Promise.all([fetchMemory(), fetchShareInfo()]).finally(
      () => setLoading(false),
    );
  // defaultEditMode intentionally excluded — we only want it on open, not on re-render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, memoryId]);

  // Sign audio URL when memory loads — handles both bare paths (new) and full URLs (legacy)
  useEffect(() => {
    if (!memory?.audio_url) return;
    const raw = memory.audio_url;
    let path: string | null;
    if (!raw.startsWith("http")) {
      path = raw.split("?")[0];
    } else {
      const marker = "/audio-memories/";
      const idx = raw.indexOf(marker);
      path = idx !== -1 ? raw.slice(idx + marker.length).split("?")[0] : null;
    }
    if (!path) return;
    supabase.storage.from("audio-memories").createSignedUrl(path, 3600)
      .then(({ data }) => { if (data) setSignedAudioUrl(data.signedUrl); });
  }, [memory?.audio_url]);

  // Sign image URL when memory loads
  useEffect(() => {
    if (!memory?.image_url) return;
    supabase.storage.from("memory-images").createSignedUrl(memory.image_url, 3600)
      .then(({ data }) => { if (data) setSignedImageUrl(data.signedUrl); });
  }, [memory?.image_url]);

  // Sign context audio URL when memory loads
  useEffect(() => {
    if (!memory?.context_audio_url) return;
    const path = memory.context_audio_url.startsWith("http")
      ? (() => { const idx = memory.context_audio_url!.indexOf("/audio-memories/"); return idx !== -1 ? memory.context_audio_url!.slice(idx + "/audio-memories/".length).split("?")[0] : null; })()
      : memory.context_audio_url.split("?")[0];
    if (!path) return;
    supabase.storage.from("audio-memories").createSignedUrl(path, 3600)
      .then(({ data }) => { if (data) setSignedContextAudioUrl(data.signedUrl); });
  }, [memory?.context_audio_url]);

  // Fetch card context (title + other times)
  useEffect(() => {
    if (!memory?.content_library_id) {
      setCardTitle(null);
      setOtherTimes([]);
      return;
    }
    const clId = memory.content_library_id;
    const childIds = memory.memory_participants
      .filter((mp) => mp.child_id !== null)
      .map((mp) => mp.child_id!);

    void fetchCardContext(clId, childIds, memory.id).then(({ cardTitle, otherTimes }) => {
      setCardTitle(cardTitle);
      setOtherTimes(otherTimes);
    });
  }, [memory?.content_library_id, memory?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Realtime subscription while sheet is open
  useEffect(() => {
    if (!isOpen || !memoryId) return;
    const channel = supabase
      .channel(`memory-detail:${memoryId}`)
      .on("postgres_changes", {
        event: "UPDATE",
        schema: "public",
        table: "memories",
        filter: `id=eq.${memoryId}`,
      }, () => void fetchMemory())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [isOpen, memoryId, fetchMemory]);

  // Collection options fetch removed — the picker returns in Chat 9
  // reading from the collection_memories junction.

  // ── Auto-save ─────────────────────────────────────────────────────────────
  const markSaved = () => {
    setSaveStatus("saved");
    if (savedTimer.current) clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSaveStatus("idle"), 2000);
  };

  const autoSave = useCallback(async (patch: Record<string, unknown>) => {
    setSaveStatus("saving");
    const { error } = await supabase
      .from("memories")
      .update(patch as never)
      .eq("id", memoryId);
    if (error) {
      setSaveStatus("error");
      return;
    }
    markSaved();
    void invalidateMemories();
  }, [memoryId]);

  // ── Field handlers ────────────────────────────────────────────────────────
  const handleTitleChange = (val: string) => {
    setTitle(val);
    if (titleTimer.current) clearTimeout(titleTimer.current);
    titleTimer.current = setTimeout(() => {
      void autoSave({ title: val.trim() || null });
    }, 800);
  };

  const handleChapterChange = (val: string) => {
    setAiChapter(val);
    if (chapterTimer.current) clearTimeout(chapterTimer.current);
    chapterTimer.current = setTimeout(() => {
      void autoSave({ ai_chapter: val.trim() || null });
    }, 800);
  };

  const handleCategoryToggle = (cat: string) => {
    const next = categories.includes(cat)
      ? categories.filter((c) => c !== cat)
      : [...categories, cat];
    setCategories(next);
    void autoSave({ categories: next });
  };

  const handleMemoryDateChange = (iso: string) => {
    if (!iso) return;
    setMemoryDateIso(iso);
    // Parse as local midnight so the stored date matches what the user picked
    // regardless of timezone. Future dates are prevented by `max` on the input.
    const [y, m, d] = iso.split("-").map(Number);
    const next = new Date(y, m - 1, d);
    void autoSave({ memory_date: next.toISOString() });
  };

  const handleRemoveChild = async (childId: string) => {
    const prev = taggedChildIds;
    setTaggedChildIds((ids) => ids.filter((id) => id !== childId));
    setSaveStatus("saving");
    const { error } = await supabase
      .from("memory_participants")
      .delete()
      .eq("memory_id", memoryId)
      .eq("child_id", childId);
    if (error) {
      setSaveStatus("error");
      setTaggedChildIds(prev);
      return;
    }
    markSaved();
    void invalidateMemories();
  };

  const handleAddChild = async (childId: string) => {
    const prev = taggedChildIds;
    setTaggedChildIds((ids) => [...ids, childId]);
    setSaveStatus("saving");
    const { error } = await supabase
      .from("memory_participants")
      .insert({ memory_id: memoryId, child_id: childId });
    if (error) {
      setSaveStatus("error");
      setTaggedChildIds(prev);
      return;
    }
    markSaved();
    void invalidateMemories();
  };

  const handleChildIdsChange = async (next: string[]) => {
    const current = taggedChildIds;
    const added = next.filter((id) => !current.includes(id));
    const removed = current.filter((id) => !next.includes(id));
    for (const id of added) await handleAddChild(id);
    for (const id of removed) await handleRemoveChild(id);
  };

  // Current collection link (single-select — 0% multi-membership as of Chat 9).
  const currentCollectionId =
    memory?.collection_memories?.[0]?.collection_id ?? null;

  const handleCollectionChange = async (nextCollectionId: string | null) => {
    if (!memory) return;
    if (nextCollectionId === currentCollectionId) return;
    const prev = memory.collection_memories;
    setSaveStatus("saving");

    // Optimistic: update the local memory object so the chip redraws instantly.
    const optimistic = nextCollectionId
      ? (() => {
          const hit = collections.find((c) => c.id === nextCollectionId);
          return hit
            ? [
                {
                  collection_id: hit.id,
                  collection: { id: hit.id, name: hit.name, icon: hit.icon, color: hit.color },
                },
              ]
            : [];
        })()
      : [];
    setMemory((m) => (m ? { ...m, collection_memories: optimistic } : m));

    // Insert-first-then-delete-others keeps the single-select invariant while
    // avoiding the race in the reverse order. If delete succeeds and insert
    // then fails, the DB is cleared but the UI rolls back to the old link —
    // an invisible inconsistency until the next fetch. Inserting first means:
    //   - insert fails → no DB change, rollback is a no-op for the DB
    //   - insert succeeds, delete fails → memory has the NEW link (intended
    //     end state) plus possibly an old one; next fetch will show the new
    //     one since the picker is single-select (first chip wins)
    // Idempotent via ignoreDuplicates so retapping the same collection is safe.
    if (nextCollectionId) {
      const { error: insErr } = await supabase
        .from("collection_memories")
        .upsert(
          {
            collection_id: nextCollectionId,
            memory_id: memoryId,
            added_by: "manual",
          },
          { onConflict: "collection_id,memory_id", ignoreDuplicates: true },
        );
      if (insErr) {
        setSaveStatus("error");
        setMemory((m) => (m ? { ...m, collection_memories: prev } : m));
        return;
      }
    }

    // Delete any OTHER links for this memory. When clearing (nextCollectionId
    // is null), this deletes all links.
    let delQuery = supabase
      .from("collection_memories")
      .delete()
      .eq("memory_id", memoryId);
    if (nextCollectionId) {
      delQuery = delQuery.neq("collection_id", nextCollectionId);
    }
    const { error: delErr } = await delQuery;
    if (delErr) {
      setSaveStatus("error");
      setMemory((m) => (m ? { ...m, collection_memories: prev } : m));
      return;
    }

    markSaved();
    // Bust the collections cache so entry counts refresh on the Collections page
    // and re-fetch the feed so any collection-filtered view reflects the change.
    refreshCollections();
    void invalidateMemories();
  };

  const handleParticipantsChange = async (next: ParticipantRef[]) => {
    const prev = participants;
    setParticipants(next);
    setSaveStatus("saving");
    // Delete non-child participant rows only — child rows are managed by
    // handleAddChild/handleRemoveChild and must not be wiped here.
    const { error: delErr } = await supabase
      .from("memory_participants")
      .delete()
      .eq("memory_id", memoryId)
      .is("child_id", null);
    if (delErr) {
      setSaveStatus("error");
      setParticipants(prev);
      return;
    }
    // Owner is implicit via memories.user_id — skip owner refs.
    const rows = next
      .filter((p) => p.kind !== "owner")
      .map((p) => {
        if (p.kind === "connection") {
          // connectionId is space_members.id; unregistered members use display_name.
          return p.profileId
            ? { memory_id: memoryId, space_member_id: p.connectionId }
            : { memory_id: memoryId, display_name: p.name };
        }
        return { memory_id: memoryId, display_name: (p as { name: string }).name };
      });
    if (rows.length > 0) {
      const { error: insErr } = await supabase.from("memory_participants").insert(rows);
      if (insErr) {
        setSaveStatus("error");
        setParticipants(prev);
        return;
      }
    }
    markSaved();
    void invalidateMemories();
  };

  const handleDeleteAudio = async () => {
    if (!memory?.audio_url) return;
    const raw = memory.audio_url;
    let storagePath: string | null;
    if (!raw.startsWith("http")) {
      storagePath = raw.split("?")[0];
    } else {
      const idx = raw.indexOf("/audio-memories/");
      storagePath = idx !== -1 ? raw.slice(idx + "/audio-memories/".length).split("?")[0] : null;
    }
    if (storagePath) {
      await supabase.storage.from("audio-memories").remove([storagePath]);
    }
    const { error } = await supabase
      .from("memories")
      .update({ audio_url: null })
      .eq("id", memoryId);
    if (error) { toast.error(t("memory.detail.deleteRecordingError")); return; }
    setMemory((m) => m ? { ...m, audio_url: null } : m);
    setSignedAudioUrl(null);
    setDeleteAudioOpen(false);
    toast.success(t("memory.detail.deleteRecordingSuccess"));
    void invalidateMemories();
  };


  const handleDeleteMemory = async () => {
    // Capture estimated_storage_mb + space_id from the deleted row so we can
    // decrement the space owner's profiles.storage_used_mb immediately. RLS
    // already restricts deletes to permitted users. The RPC resolves the
    // owner from the space_id.
    const { data: deletedRows, error } = await supabase
      .from("memories")
      .delete()
      .eq("id", memoryId)
      .select("estimated_storage_mb, space_id");
    if (error) { toast.error(t("memory.detail.deleteMemoryError")); return; }

    const deleted = (deletedRows ?? [])[0] as
      | { estimated_storage_mb: number | null; space_id: string }
      | undefined;
    const delta = Number(deleted?.estimated_storage_mb ?? 0);
    if (deleted && delta > 0) {
      // Fire-and-forget: daily cron reconciles any drift on failure.
      const { error: adjErr } = await supabase.rpc("adjust_space_storage_used", {
        p_space_id: deleted.space_id,
        p_delta_mb: -delta,
      });
      if (adjErr) {
        console.error("[MemoryDetailSheet] storage decrement failed", adjErr);
      }
    }

    setDeleteMemoryOpen(false);
    onClose();
    onDelete?.();
    void invalidateMemories();
    void invalidateEntitlements();
    toast.success(t("memory.detail.deleteMemorySuccess"));
  };

  const handleShare = async () => {
    if (!user) return;

    // Check for an existing active share first — reuse it rather than creating duplicates.
    const existing = await fetchActiveShare(memoryId, user.id);

    let token: string;
    let shareType: "new" | "reused";

    if (existing) {
      token = (existing as { token: string }).token;
      shareType = "reused";
    } else {
      // Upsert — handles both fresh inserts and renewing expired/revoked rows.
      // expires_at is set explicitly so the UPDATE path also refreshes the expiry.
      const newExpiry = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
      const { data: created, error } = await supabase
        .from("shared_memories")
        .upsert(
          {
            memory_id: memoryId,
            created_by: user.id,
            expires_at: newExpiry,
            revoked_at: null,
          },
          { onConflict: "memory_id,created_by" },
        )
        .select("token")
        .single();
      if (error || !created) {
        toast.error(t("memory.detail.shareError"));
        return;
      }
      token = (created as { token: string }).token;
      shareType = "new";
    }

    const url = `${window.location.origin}/s/${token}`;
    const shareData = {
      title: t("share.title"),
      text: t("share.text"),
      url,
    };

    try {
      if (
        typeof navigator.share === "function" &&
        navigator.canShare?.(shareData)
      ) {
        await navigator.share(shareData);
        // Native share sheet — no toast needed.
      } else {
        await navigator.clipboard.writeText(url);
        toast.success(t("share.copied"));
      }
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") {
        // User dismissed the native share sheet — do nothing.
        return;
      }
      // Fallback to clipboard if navigator.share() fails for other reasons.
      try {
        await navigator.clipboard.writeText(url);
        toast.success(t("share.copied"));
      } catch {
        toast.error(t("memory.detail.shareError"));
        return;
      }
    }

    fireEvent("memory.shared", { userId: user.id, memoryId, shareToken: token, shareType });
    // Refresh share status in the bottom bar.
    void fetchShareInfo();
  };

  const handleRevoke = async () => {
    if (!user) return;
    setRevokeLoading(true);
    const { error } = await supabase
      .from("shared_memories")
      .update({ revoked_at: new Date().toISOString() })
      .eq("memory_id", memoryId)
      .eq("created_by", user.id);
    setRevokeLoading(false);
    if (error) {
      toast.error(t("share.revokeError"));
      return;
    }
    setShareInfo(null);
    toast.success(t("share.revokeSuccess"));
  };

  const handleRetryAi = async () => {
    if (!memoryId || retryAiLoading) return;
    const ok = await ensureAiOrPrompt();
    if (!ok) return;
    setRetryAiLoading(true);
    try {
      const result = await retryAiProcessing(memoryId);
      if (result.status === "exhausted") setRetryExhausted(true);
    } catch {
      toast.error(t("memory.detail.storyFailedRetryError"));
    } finally {
      setRetryAiLoading(false);
    }
  };

  // ── Derived display values ────────────────────────────────────────────────
  const taggedChildren = allChildren.filter((c) => taggedChildIds.includes(c.id));

  const creatorName = memory?.contributor_name ?? "you";
  const noAiChapter = !aiChapter.trim();

  // Viewer role — drives which visibility UI (if any) renders.
  const viewerIsOwner = !!user && !!memory && memory.user_id === user.id;
  const viewerIsMember =
    !!user && coparents.some((cp) => cp.profileId === user.id);

  // ── Role-based affordance gating ─────────────────────────────────────────
  // Child viewers get a narrow contract:
  //   - Own memory (user_id === auth.uid) → title/transcript edit + delete.
  //   - Family memory they're tagged in → playback + image only, read-only.
  // Everything else (share, collection picker, audience toggle, participant
  // editor, category/date editors, audio delete, "add contributor") is
  // hidden unconditionally in child mode. See Prompt 6 in the role rollout.
  const isChildViewer = activeSpaceRole === "child";
  const isOwnMemory = viewerIsOwner;
  // In child-tagged mode we never let editMode stick — force read.
  const canEditThisMemory = !isChildViewer || isOwnMemory;
  const hideParticipantEditor = isChildViewer;
  const hideCollectionPicker = isChildViewer;
  const hideAudienceControls = isChildViewer;
  const hideDateEditor = isChildViewer;
  const hideCategoriesEditor = isChildViewer;
  const hideAudioDelete = isChildViewer;
  const hideShareAction = isChildViewer;
  const hideDeleteAction = isChildViewer && !isOwnMemory;

  // Defence in depth: even if a caller passed defaultEditMode=true or a stale
  // editMode survived an auth/role flip, never let a non-editor land in edit.
  useEffect(() => {
    if (!canEditThisMemory && editMode) setEditMode(false);
  }, [canEditThisMemory, editMode]);

  // Audience axis: tagged non-members lose access when audience='members'.
  // Legacy rows (column added later) may be null — treat as 'space' (DB default).
  const currentAudience: "members" | "space" = memory?.audience ?? "space";

  // Tagged non-members = contributors today + tagged children WITH an active
  // Kindle-kid login. Both groups' access depends on audience='space' — the
  // toggle gates them together. Tagged children who aren't promoted yet are
  // excluded because audience has no observable effect on them. Mirrors the
  // capture flow in MemoryMetadataScreen.
  const taggedContributors = participants.filter(
    (p): p is Extract<ParticipantRef, { kind: "connection" }> =>
      p.kind === "connection" && p.role === "contributor",
  );
  const taggedChildrenWithAccount = taggedChildren.filter(
    (c) => c.hasActiveChildAccount,
  );
  const shareableNames = [
    ...taggedContributors.map((p) => p.name),
    ...taggedChildrenWithAccount.map((c) => c.name),
  ];
  const hasShareables = shareableNames.length > 0;

  // Keep the local toggle honest when the memory (re)loads or changes audience
  // via a realtime UPDATE — otherwise the switch would drift from the DB.
  useEffect(() => {
    setIsSharedWithTagged(currentAudience !== "members");
  }, [currentAudience]);

  // ── Visibility handlers ──────────────────────────────────────────────────
  const handleSharedWithTaggedChange = (next: boolean) => {
    if (next) {
      // OFF → ON: access-granting, no confirmation.
      setIsSharedWithTagged(true);
      void autoSave({ audience: "space" });
    } else {
      // ON → OFF: access-removing, confirm first. Do NOT update state yet —
      // the switch stays ON visually until the user confirms.
      setRemoveAccessDialogOpen(true);
    }
  };

  const handleConfirmRemoveAccess = () => {
    setIsSharedWithTagged(false);
    void autoSave({ audience: "members" });
    setRemoveAccessDialogOpen(false);
  };

  const removeAccessBody = (() => {
    const names = shareableNames;
    if (names.length === 0) return "";
    if (names.length === 1) return t("dialog.removeAccessBody_1", { name: names[0] });
    if (names.length === 2) return t("dialog.removeAccessBody_2", { name1: names[0], name2: names[1] });
    const rest = names.length - 2;
    return t("dialog.removeAccessBody_n", { count: rest, name1: names[0], name2: names[1] });
  })();

  // ── Save status label ─────────────────────────────────────────────────────
  const saveStatusLabel = {
    idle: null,
    saving: <span className="text-[11px] text-muted-foreground">{t("memory.detail.saving")}</span>,
    saved: <span className="text-[11px] text-emerald-600 dark:text-emerald-400">{t("memory.detail.saved")}</span>,
    error: <span className="text-[11px] text-destructive">{t("memory.detail.saveFailed")}</span>,
  }[saveStatus];

  // ── Shared inner content ─────────────────────────────────────────────────

  const topBarContent = (
    <div className="flex-none flex items-start justify-between px-4 pt-3 pb-2 relative">
      {/* Close / Back */}
      {fullPage ? (
        <button
          onClick={onClose}
          className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-muted transition-colors text-muted-foreground"
          aria-label={t("aria.back")}
        >
          <X className="h-4 w-4" />
        </button>
      ) : (
        <SheetPrimitive.Close asChild>
          <button
            className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-muted transition-colors text-muted-foreground"
            aria-label={t("aria.close")}
          >
            <X className="h-4 w-4" />
          </button>
        </SheetPrimitive.Close>
      )}

      {/* Right cluster: heart + edit + save status */}
      <div className="flex items-start gap-1">
        {memory && (
          <button
            type="button"
            onClick={() => {
              const next = !memory.is_favorited;
              // Mirror the optimistic update onto local state so the heart
              // flips instantly inside the sheet (the React Query cache holds
              // the list-level copy, but this sheet keeps its own state).
              setMemory((m) => (m ? { ...m, is_favorited: next } : m));
              toggleFavorite.mutate({
                memoryId: memory.id,
                isFavorited: memory.is_favorited,
              });
            }}
            className={`flex h-8 w-8 items-center justify-center rounded-full transition-colors ${
              memory.is_favorited
                ? "text-rose-500 hover:bg-rose-50"
                : "text-muted-foreground hover:text-rose-500 hover:bg-muted"
            }`}
            aria-label={memory.is_favorited ? "Remove favorite" : "Add to favorites"}
            aria-pressed={memory.is_favorited}
          >
            <Heart
              className="h-4 w-4"
              fill={memory.is_favorited ? "currentColor" : "none"}
            />
          </button>
        )}

        {/* Edit / Done + save status. Hidden in child-tagged mode — read-only. */}
        <div className="flex flex-col items-end gap-0.5">
          {canEditThisMemory && (
            <button
              onClick={() => setEditMode((e) => !e)}
              className="text-sm font-medium text-primary px-2 py-1 rounded-lg hover:bg-primary/10 transition-colors"
            >
              {editMode ? t("memory.detail.done") : t("memory.detail.edit")}
            </button>
          )}
          <div className="h-4 flex items-center">{saveStatusLabel}</div>
        </div>
      </div>
    </div>
  );

  const scrollableContent = (
    <div className="flex-1 overflow-y-auto px-5 space-y-5 pb-4">
              {loading || !memory ? (
                /* Skeleton */
                <div className="space-y-4 pt-2">
                  <div className="h-7 w-3/4 rounded-lg bg-muted animate-pulse" />
                  <div className="h-4 w-1/2 rounded-lg bg-muted animate-pulse" />
                  <div className="h-32 w-full rounded-xl bg-muted animate-pulse" />
                  <div className="h-4 w-2/3 rounded-lg bg-muted animate-pulse" />
                  <div className="h-4 w-1/2 rounded-lg bg-muted animate-pulse" />
                </div>
              ) : (
                <>
                  {/* 0 ── Shortie Video ─────────────────────────────────── */}
                  {memory.memory_type === "shortie" && memory.video_url && (
                    <div className="space-y-1.5">
                      <ShortiePlayer
                        videoUrl={memory.video_url}
                        thumbnailUrl={memory.thumbnail_url}
                        duration={memory.video_duration_seconds}
                      />
                    </div>
                  )}

                  {/* 1 ── Photo carousel ─────────────────────────────── */}
                  {memory.image_url && (
                    <DataView
                      isLoading={artefactsLoading}
                      isError={artefactsIsError}
                      error={artefactsError}
                      isEmpty={false}
                      skeleton={<div className="w-full rounded-xl bg-muted/30 animate-pulse" style={{ height: 200 }} />}
                      onRetry={() => void refetchArtefacts()}
                    >
                      <div className="space-y-1.5">
                        {artefacts.length > 0 ? (
                          <ArtefactCarousel
                            artefacts={artefacts}
                            onTap={(url) => setLightboxSrc(url)}
                          />
                        ) : signedImageUrl ? (
                          <button
                            type="button"
                            onClick={() => setLightboxSrc(signedImageUrl)}
                            className="w-full rounded-xl overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                            aria-label={t("aria.viewFullScreen")}
                          >
                            <img
                              src={signedImageUrl}
                              alt={t("alt.memoryImage")}
                              className="w-full object-cover rounded-xl"
                              style={{ maxHeight: 240 }}
                            />
                          </button>
                        ) : null}
                        {lightboxSrc !== null && (
                          <FullScreenImage
                            src={lightboxSrc}
                            isOpen
                            onClose={() => setLightboxSrc(null)}
                          />
                        )}
                      </div>
                    </DataView>
                  )}

                  {/* 1b ── Voice artifact (voice_moment / time_capsule) ── */}
                  {memory.audio_url && (memory.memory_type === "voice_moment" || memory.memory_type === "time_capsule") && (
                    <div className="rounded-xl bg-muted/60 border border-border/40 px-4 py-3 space-y-3">
                      <div className="space-y-2">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                          {t("memory.detail.recordingLabel")}
                        </p>
                        <audio
                          controls
                          preload="metadata"
                          src={signedAudioUrl ?? memory.audio_url}
                          style={{ display: "block", width: "100%", height: 36 }}
                        />
                        {editMode && !hideAudioDelete && (
                          <button
                            onClick={() => setDeleteAudioOpen(true)}
                            className="text-xs text-muted-foreground underline underline-offset-2 hover:text-destructive transition-colors self-start"
                          >
                            {t("memory.detail.deleteAudio")}
                          </button>
                        )}
                      </div>
                      {/* Context note recorded alongside the main voice artifact */}
                      {memory.context_audio_url && (
                        <div className="space-y-2 pt-1 border-t border-border/30">
                          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                            {t("memory.detail.contextRecording")}
                          </p>
                          <audio
                            controls
                            preload="metadata"
                            src={signedContextAudioUrl ?? memory.context_audio_url}
                            style={{ display: "block", width: "100%", height: 36 }}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* 1c ── Voice input for general_memory (ephemeral — deleted after 30 days) */}
                  {memory.audio_url && memory.memory_type === "general_memory" && (
                    <div className="space-y-1.5">
                      <button
                        onClick={() => setVoiceNoteOpen((v) => !v)}
                        className="flex w-full items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {voiceNoteOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                        {voiceNoteOpen ? t("memory.detail.transcriptHide") : t("memory.detail.voiceNote")}
                      </button>
                      {voiceNoteOpen && (
                        <audio
                          controls
                          preload="metadata"
                          src={signedAudioUrl ?? memory.audio_url}
                          style={{ display: "block", width: "100%", height: 32 }}
                        />
                      )}
                    </div>
                  )}

                  {/* 2 ── Title ─────────────────────────────────────────── */}
                  {editMode ? (
                    <input
                      value={title}
                      onChange={(e) => handleTitleChange(e.target.value)}
                      placeholder={t("memory.detail.titlePlaceholder")}
                      autoFocus
                      className="w-full text-[20px] font-medium text-foreground bg-muted/50 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30 border border-border/50"
                    />
                  ) : (
                    <p className="text-[20px] font-medium text-foreground leading-snug">
                      {title || (memory && getMemoryAutoTitle(memory)) || (
                        <span className="text-muted-foreground italic">
                          {memory?.ai_status === "skipped"
                            ? t("memory.detail.skippedTitleFallback", {
                                date: formatDay(memory.memory_date ?? memory.created_at),
                              })
                            : t("memory.detail.untitled")}
                        </span>
                      )}
                    </p>
                  )}

                  {/* 3 ── Meta row. Date becomes editable in edit mode. */}
                  <div className="flex items-center gap-1 flex-wrap text-[13px] text-muted-foreground">
                    <span>{t("memory.detail.by", { name: creatorName })}</span>
                    <span>·</span>
                    {editMode && !hideDateEditor ? (
                      <input
                        type="date"
                        value={memoryDateIso}
                        max={new Date().toISOString().slice(0, 10)}
                        onChange={(e) => handleMemoryDateChange(e.target.value)}
                        className="rounded-md bg-muted/50 border border-border/50 px-2 py-0.5 text-[13px] text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                      />
                    ) : (
                      <span>{formatFull(memory.memory_date ?? memory.created_at)}</span>
                    )}
                    {memory.location && (
                      <>
                        <span>·</span>
                        <span className="flex items-center gap-0.5">
                          <MapPin className="h-3 w-3" />
                          {memory.location}
                        </span>
                      </>
                    )}
                  </div>

                  {/* 3a ── "Captured on" subtitle. Only shown when the memory
                     is backdated (memory_date differs from created_at by a
                     calendar day) — same-day captures stay clean. */}
                  {memory.memory_date &&
                    isDifferentCaptureDay(memory.memory_date, memory.created_at) && (
                      <p className="text-[11px] text-muted-foreground/70 -mt-1">
                        {t("memory.detail.capturedOn", {
                          date: formatDay(memory.created_at),
                        })}
                      </p>
                    )}

                  {/* 3b ── Challenge badge ──────────────────────────────── */}
                  {memory.content_library_id && cardTitle && (
                    <span
                      className="inline-flex items-center self-start"
                      style={{
                        backgroundColor: "#EEEDFE",
                        color: "#534AB7",
                        border: "0.5px solid #AFA9EC",
                        borderRadius: 20,
                        padding: "4px 12px",
                        fontSize: 12,
                        fontWeight: 500,
                      }}
                    >
                      🎯 {cardTitle}
                    </span>
                  )}

                  {/* 4 ── Who is this about ────────────────────────────────
                     * Edit mode: one unified picker (children + ME + tribe).
                     * Read-only: compact chip display, split into tagged
                     * children + "who else was there" for readability. */}
                  <div className="space-y-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {t("memory.detail.whoAbout")}
                    </p>
                    {editMode && !hideParticipantEditor ? (
                      <ParticipantPicker
                        mode="memory"
                        value={participants}
                        onChange={(next) => void handleParticipantsChange(next)}
                        taggableSubjects={taggableSubjects}
                        selectedSubjectIds={taggedChildIds}
                        onSubjectIdsChange={(next) => void handleChildIdsChange(next)}
                      />
                    ) : (() => {
                      const ownerTagged = participants.some((p) => p.kind === "owner");
                      const nonOwnerParticipants = participants.filter((p) => p.kind !== "owner");
                      const nothingTagged =
                        taggedChildren.length === 0 && nonOwnerParticipants.length === 0 && !ownerTagged;
                      return (
                        <div className="flex flex-wrap gap-2 items-center">
                          {taggedChildren.map((child) => (
                            <span
                              key={child.id}
                              className="flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium bg-muted text-foreground"
                            >
                              <ChildAvatar child={child} size={20} />
                              {child.name}
                            </span>
                          ))}
                          {ownerTagged && viewerIsOwner && (
                            <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-foreground">
                              {owner?.nickname ? `You · ${owner.nickname}` : "You"}
                            </span>
                          )}
                          {nothingTagged && (
                            <span className="text-xs text-muted-foreground">{t("memory.detail.noOneTagged")}</span>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  {/* 4b ── Who else was there (read-only only) ─────────────
                     * Shows tagged tribe members beyond the owner. Hidden
                     * entirely when only the implicit owner is tagged — the
                     * "You" chip in the section above already communicates it. */}
                  {!editMode && participants.some((p) => p.kind !== "owner") && (
                    <div className="space-y-2">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Who else was there
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {participants
                          .filter((p) => p.kind !== "owner")
                          .map((p, i) => {
                            const label = p.kind === "connection" ? p.name : p.name;
                            return (
                              <span
                                key={`${p.kind}-${i}`}
                                className="inline-flex items-center rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-foreground"
                              >
                                {label}
                              </span>
                            );
                          })}
                      </div>
                    </div>
                  )}

                  {/* 4c ── Visibility ──────────────────────────────────────
                     * Owner edit mode: toggle (when tagged contributors exist)
                     *   + live readout.
                     * Owner view mode + non-owner member: readout only.
                     * Contributors and other viewers: nothing — they already
                     * have access via RLS and the framing is not for them. */}
                  {(viewerIsOwner || viewerIsMember) && !hideAudienceControls && (
                    <div className="space-y-3">
                      {editMode && viewerIsOwner && hasShareables && (
                        <ShareWithTaggedToggle
                          names={shareableNames}
                          checked={isSharedWithTagged}
                          onCheckedChange={handleSharedWithTaggedChange}
                        />
                      )}
                      <VisibilityReadout
                        participants={participants}
                        isSharedWithTagged={
                          editMode && viewerIsOwner
                            ? isSharedWithTagged
                            : currentAudience !== "members"
                        }
                        taggedChildren={taggedChildren.map((c) => ({
                          id: c.id,
                          name: c.name,
                          hasActiveChildAccount: c.hasActiveChildAccount,
                        }))}
                      />
                    </div>
                  )}

                  {/* 5 ── Categories ─────────────────────────────────────── */}
                  <div className="space-y-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Categories
                    </p>
                    {editMode && !hideCategoriesEditor ? (
                      <div className="flex flex-wrap gap-1.5">
                        {activeCategories.map((cat) => {
                          const active = categories.includes(cat.label);
                          return (
                            <button
                              key={cat.key}
                              onClick={() => handleCategoryToggle(cat.label)}
                              className={`rounded-full px-2.5 py-1 text-xs font-medium border transition-all ${
                                active
                                  ? (categoryColorClass[cat.label] ?? fallbackCategoryStyle)
                                  : "bg-muted/50 text-muted-foreground border-border/50 hover:border-border"
                              }`}
                            >
                              {cat.emoji} {cat.label}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {categories.length > 0 ? (
                          categories.map((cat) => {
                            const { emoji } = getCategoryStyle(cat);
                            return (
                              <Badge
                                key={cat}
                                className={categoryColorClass[cat] ?? fallbackCategoryStyle}
                                variant="secondary"
                              >
                                {emoji} {cat}
                              </Badge>
                            );
                          })
                        ) : (
                          <span className="text-xs text-muted-foreground">No categories</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* 7 ── Collection ───────────────────────────────────────
                     * Edit mode: unified picker (same component as the capture
                     *   flow) writing to the `collection_memories` junction.
                     * Read mode: chip from the junction's embed — hidden when
                     *   no collection is linked to keep the sheet tidy.
                     * Child viewers never see the picker (Prompt 6) — the
                     * read-mode chip stays as passive context. */}
                  {editMode && !hideCollectionPicker ? (
                    <div className="space-y-2">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {t("memory.metadata.collectionSection")}
                      </p>
                      <CollectionPicker
                        value={currentCollectionId}
                        onChange={(next) => void handleCollectionChange(next)}
                        collections={collections}
                        prefillSource={null}
                        prefillCollectionId={null}
                        onCollectionCreated={refreshCollections}
                      />
                    </div>
                  ) : (
                    memory?.collection_memories && memory.collection_memories.length > 0 && (() => {
                      const collectionRef = memory.collection_memories[0].collection;
                      if (!collectionRef) return null;
                      return (
                        <div className="space-y-2">
                          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                            {t("memory.metadata.collectionSection")}
                          </p>
                          <span
                            className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full"
                            style={{
                              backgroundColor: `${collectionRef.color}20`,
                              color: collectionRef.color,
                              border: `1px solid ${collectionRef.color}40`,
                            }}
                          >
                            <span>{collectionRef.icon}</span>
                            <span>{collectionRef.name}</span>
                          </span>
                        </div>
                      );
                    })()
                  )}

                  {/* 8 ── AI Summary ─────────────────────────────────────── */}
                  <div className="space-y-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Story
                    </p>
                    {editMode ? (
                      <textarea
                        value={aiChapter}
                        onChange={(e) => {
                          handleChapterChange(e.target.value);
                          // Auto-expand height
                          const el = e.currentTarget;
                          el.style.height = "auto";
                          el.style.height = `${el.scrollHeight}px`;
                        }}
                        placeholder="AI story will appear here after processing…"
                        rows={4}
                        style={{ resize: "none", minHeight: 100 }}
                        className="w-full rounded-lg border border-border/50 bg-muted/50 px-3 py-2 text-sm leading-relaxed text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                      />
                    ) : memory?.ai_status === "failed" ? (
                      <div className="space-y-2">
                        <p className="text-sm text-muted-foreground italic">
                          {t("memory.detail.storyFailed")}
                        </p>
                        {retryExhausted || (memory.ai_retry_count ?? 0) >= 3 ? (
                          <p className="text-sm text-muted-foreground italic">
                            {t("memory.detail.storyFailedExhausted")}
                          </p>
                        ) : (
                          <button
                            type="button"
                            disabled={retryAiLoading}
                            onClick={() => void handleRetryAi()}
                            className="text-sm font-medium text-primary hover:opacity-80 disabled:opacity-50 transition-opacity"
                          >
                            {retryAiLoading ? "…" : t("memory.detail.storyFailedRetry")}
                          </button>
                        )}
                      </div>
                    ) : memory?.ai_status === "skipped" && noAiChapter ? (
                      <div className="space-y-2">
                        <p className="text-sm text-muted-foreground italic">
                          {t("memory.detail.storySkipped")}
                        </p>
                        {!retryExhausted && (memory.ai_retry_count ?? 0) < 3 && (
                          <button
                            type="button"
                            disabled={retryAiLoading}
                            onClick={() => void handleRetryAi()}
                            className="text-sm font-medium text-primary hover:opacity-80 disabled:opacity-50 transition-opacity"
                          >
                            {retryAiLoading
                              ? t("memory.detail.storyTranscribedLoading")
                              : t("memory.detail.storyTranscribedCta")}
                          </button>
                        )}
                      </div>
                    ) : memory?.ai_status === "transcribed" && noAiChapter ? (
                      <div className="space-y-2">
                        <p className="text-sm text-muted-foreground italic">
                          {t("memory.detail.storyTranscribed")}
                        </p>
                        <button
                          type="button"
                          disabled={retryAiLoading}
                          onClick={() => void handleRetryAi()}
                          className="text-sm font-medium text-primary hover:opacity-80 disabled:opacity-50 transition-opacity"
                        >
                          {retryAiLoading
                            ? t("memory.detail.storyTranscribedLoading")
                            : t("memory.detail.storyTranscribedCta")}
                        </button>
                      </div>
                    ) : noAiChapter ? (
                      <p className="text-sm text-muted-foreground italic">
                        {t("memory.detail.storyProcessing")}
                      </p>
                    ) : (
                      <p
                        className="text-sm text-foreground"
                        style={{ fontFamily: "Georgia, serif", lineHeight: 1.7 }}
                      >
                        {aiChapter}
                      </p>
                    )}
                  </div>

                  {/* 8b ── Mori's comment ─────────────────────────────── */}
                  {memory.mori_comment && !editMode && (
                    <div
                      className="relative rounded-2xl px-4 py-3 pl-12"
                      style={{
                        backgroundColor: "hsl(var(--category-funny) / 0.55)",
                        border: "1px solid hsl(var(--category-funny-text) / 0.2)",
                      }}
                    >
                      <span
                        aria-hidden
                        className="absolute left-3 top-2.5 text-xl leading-none select-none"
                      >
                        🐿️
                      </span>
                      <p
                        className="text-sm italic"
                        style={{
                          color: "hsl(var(--category-funny-text))",
                          lineHeight: 1.55,
                          fontFamily: "Georgia, serif",
                        }}
                      >
                        {memory.mori_comment}
                      </p>
                    </div>
                  )}

                  {/* 8 ── Raw transcript / caption / your words ─────────── */}
                  {memory.raw_transcript && (() => {
                    const mt = memory.memory_type;
                    const label = (mt === "photo" || mt === "drawing")
                      ? t("memory.detail.captionLabel")
                      : (mt === "general_memory")
                        ? t("memory.detail.rawTranscript")
                        : t("memory.detail.transcriptShow");
                    const mono = mt !== "photo" && mt !== "drawing" && mt !== "general_memory";
                    return (
                      <div className="space-y-1.5">
                        <button
                          onClick={() => setTranscriptOpen((v) => !v)}
                          className="flex w-full items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {transcriptOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                          {transcriptOpen ? t("memory.detail.transcriptHide") : label}
                        </button>
                        {transcriptOpen && (
                          <p className={`text-xs leading-relaxed text-muted-foreground border-l-2 border-border pl-3 ${mono ? "font-mono" : ""}`}>
                            {memory.raw_transcript}
                          </p>
                        )}
                      </div>
                    );
                  })()}

                  {/* 8c ── Context recording (photo / drawing / shortie — voice_moment handled in 1b) */}
                  {memory.context_audio_url && memory.memory_type !== "voice_moment" && memory.memory_type !== "time_capsule" && (
                    <div className="space-y-1.5">
                      <button
                        onClick={() => setContextAudioOpen((v) => !v)}
                        className="flex w-full items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {contextAudioOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                        {t("memory.detail.contextRecording")}
                      </button>
                      {contextAudioOpen && (
                        <audio
                          controls
                          preload="metadata"
                          src={signedContextAudioUrl ?? memory.context_audio_url}
                          style={{ display: "block", width: "100%", height: 32 }}
                        />
                      )}
                    </div>
                  )}

                  {/* 8b ── Other times from same card ──────────────────── */}
                  {otherTimes.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {t("memory.detail.otherTimes")}
                      </p>
                      <div className="space-y-1.5">
                        {otherTimes.map((ot) => (
                          <div
                            key={ot.id}
                            className="flex items-center gap-2 rounded-lg px-2.5 py-2"
                          >
                            <span className="text-xs text-muted-foreground">
                              {new Date(ot.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                            </span>
                            {ot.title && (
                              <span className="text-xs text-foreground truncate flex-1">{ot.title}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 9 ── Voice note (photo / drawing annotation) ───────── */}
                  {memory.audio_url && (memory.memory_type === "photo" || memory.memory_type === "drawing") && (
                    <div className="space-y-1.5">
                      <button
                        onClick={() => setVoiceNoteOpen((v) => !v)}
                        className="flex w-full items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {voiceNoteOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                        {voiceNoteOpen ? t("memory.detail.transcriptHide") : t("memory.detail.voiceNote")}
                      </button>
                      {voiceNoteOpen && (
                        <div className="space-y-2">
                          <audio
                            controls
                            preload="metadata"
                            src={signedAudioUrl ?? memory.audio_url}
                            style={{ display: "block", width: "100%", height: 32 }}
                          />
                          {editMode && !hideAudioDelete && (
                            <button
                              onClick={() => setDeleteAudioOpen(true)}
                              className="text-xs text-muted-foreground underline underline-offset-2 hover:text-destructive transition-colors self-start"
                            >
                              {t("memory.detail.deleteAudio")}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
  );

  // Share status for the bottom bar
  const shareDaysLeft = shareInfo
    ? Math.ceil(
        (new Date(shareInfo.expiresAt).getTime() - Date.now()) /
          (1000 * 60 * 60 * 24),
      )
    : null;
  const shareViewsLabel = shareInfo
    ? shareInfo.viewCount === 1
      ? t("share.statusViews_one", { count: 1 })
      : t("share.statusViews_other", { count: shareInfo.viewCount })
    : null;
  const shareDaysLabel = shareDaysLeft !== null
    ? shareDaysLeft === 1
      ? t("share.statusDays_one", { count: 1 })
      : t("share.statusDays_other", { count: shareDaysLeft })
    : null;

  // Child-tagged memories have no share and no delete — collapse the bar
  // entirely rather than render an empty border, so the content flows to the
  // bottom edge. Child-own memories still get Delete, just not Share.
  const bottomActions = hideShareAction && hideDeleteAction ? null : (
    <div className="flex-none border-t border-border/50 flex items-center justify-center gap-6 px-5 py-4">
      {!hideShareAction && (shareInfo ? (
        /* Active share — show status + revoke */
        <div className="flex flex-col items-center gap-0.5">
          <span className="text-xs text-muted-foreground">
            {t("share.status", { views: shareViewsLabel, days: shareDaysLabel })}
          </span>
          <button
            onClick={() => void handleRevoke()}
            disabled={revokeLoading}
            className="text-xs text-destructive underline underline-offset-2 hover:opacity-80 transition-opacity disabled:opacity-50"
          >
            {t("share.revoke")}
          </button>
        </div>
      ) : (
        /* No active share — show Share button */
        <button
          onClick={() => void handleShare()}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <Link2 className="h-4 w-4" />
          {t("share.button")}
        </button>
      ))}
      {!hideDeleteAction && (
        <button
          onClick={() => setDeleteMemoryOpen(true)}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-destructive transition-colors"
        >
          <Trash2 className="h-4 w-4" />
          {t("common.delete")}
        </button>
      )}
    </div>
  );

  const dialogs = (
    <>
      {/* Delete audio confirm */}
      <AlertDialog open={deleteAudioOpen} onOpenChange={setDeleteAudioOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("memory.detail.deleteAudioConfirm")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("memory.detail.deleteAudioBody")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handleDeleteAudio()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("memory.detail.deleteAudioAction")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Remove-access confirm — ON → OFF flip of the share-with-tagged toggle.
          Only tagged contributors lose access; members and the owner are
          unaffected regardless of this setting. */}
      <AlertDialog open={removeAccessDialogOpen} onOpenChange={setRemoveAccessDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove access?</AlertDialogTitle>
            <AlertDialogDescription>{removeAccessBody}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmRemoveAccess}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove access
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete memory confirm */}
      <AlertDialog open={deleteMemoryOpen} onOpenChange={setDeleteMemoryOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("memory.detail.deleteMemoryConfirm")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("memory.detail.deleteMemoryBody")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handleDeleteMemory()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("memory.detail.deleteMemoryAction")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AiOffBottomSheet
        open={aiSheetOpen}
        isOwner={aiIsOwner}
        enabling={aiEnabling}
        onEnableAi={onEnableAi}
        onCancel={onCancelAiSheet}
      />

    </>
  );

  // ── Render ───────────────────────────────────────────────────────────────

  // "Memory not found" lane — triggered by either an RLS deny or a genuinely
  // missing row (both indistinguishable client-side, intentionally). Rendered
  // in place of scrollableContent + bottomActions so the back button in
  // topBarContent still works; no error toast, no red screen.
  const notFoundContent = (
    <div className="flex-1 flex flex-col items-center justify-center gap-3 px-6 text-center">
      <p className="text-base font-medium text-foreground">
        {t("memory.notFound")}
      </p>
      <button
        type="button"
        onClick={onClose}
        className="text-sm text-primary hover:opacity-80 transition-opacity"
      >
        {t("common.back")}
      </button>
    </div>
  );

  if (fullPage) {
    return (
      <div className="flex flex-col h-[100dvh] bg-background animate-page-enter">
        {topBarContent}
        {notFound ? notFoundContent : (
          <>
            {scrollableContent}
            {bottomActions}
          </>
        )}
        {dialogs}
      </div>
    );
  }

  return (
    <>
      <SheetPrimitive.Root open={isOpen} onOpenChange={(v) => { if (!v) onClose(); }}>
        <SheetPortal>
          <SheetOverlay />
          <SheetPrimitive.Content
            className="fixed bottom-0 left-0 right-0 mx-auto w-full max-w-[430px] h-[92dvh] bg-background rounded-t-2xl border-t shadow-xl z-50 flex flex-col data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom data-[state=closed]:duration-300 data-[state=open]:duration-500"
          >
            <SheetPrimitive.Title className="sr-only">Memory detail</SheetPrimitive.Title>
            {topBarContent}
            {notFound ? notFoundContent : (
              <>
                {scrollableContent}
                {bottomActions}
              </>
            )}
          </SheetPrimitive.Content>
        </SheetPortal>
      </SheetPrimitive.Root>
      {dialogs}
    </>
  );
};

export default MemoryDetailSheet;
